import React, { ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import type { Location } from "history";
import ActionDialog from "./ActionDialog";
import InformationIcon from "../../images/information-icon.png";
import { CMP_POPUP_COLORS, CmpPopup, cmpPopupButton } from "./CmpDesignPopup";

interface UnsavedChangesPromptProps {
  isDirty: boolean;
  title?: string;
  description?: ReactNode;
  stayButtonText?: string;
  leaveButtonText?: string;
  /**
   * "cmp": Claude Design UNSAVED CHANGES popup (D19) for Chamber Member Pro
   * members, with Save & Update. Staff and CPA-only pages keep the default.
   */
  variant?: "default" | "cmp";
  /** CMP variant: saves the page (`to` is the page the user clicked). Resolve true to continue to it. */
  onSaveAndUpdate?: (to: string) => Promise<boolean>;
  /**
   * CMP variant (MD-N6, ACC:2793-2799): called when Save & Update did not save,
   * with a function that leaves without saving (the page's error pop-up offers it).
   */
  onSaveFailed?: (leaveWithoutSaving: () => void) => void;
}

const DEFAULT_TITLE = "UNSAVED CHANGES";
const DEFAULT_DESCRIPTION: ReactNode = (
  <>
    <div>You have unsaved changes.</div>
    <div>
      Click <strong>Update</strong> at the bottom of the page to save them.
    </div>
  </>
);

const UnsavedChangesPrompt = ({
  isDirty,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  stayButtonText = "Stay on Page",
  leaveButtonText = "Leave Without Saving",
  variant = "default",
  onSaveAndUpdate,
  onSaveFailed,
}: UnsavedChangesPromptProps): ReactElement | null => {
  const history = useHistory();
  const [pendingLocation, setPendingLocation] = useState<Location | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  // A failed Save & Update hides this popup but keeps the page the user was going to.
  const [hidden, setHidden] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // Block in-app route changes while dirty
  useEffect(() => {
    if (!isDirty) return undefined;
    const unblock = history.block((nextLocation) => {
      if (!confirmed) {
        setPendingLocation(nextLocation as Location);
        setHidden(false);
        return false;
      }
      return undefined;
    });
    return unblock;
  }, [isDirty, history, confirmed]);

  // Warn on full-page close / refresh while dirty
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Once the user confirms leaving, actually navigate to the pending location
  useEffect(() => {
    if (confirmed && pendingLocation) {
      history.push(pendingLocation.pathname + pendingLocation.search + pendingLocation.hash);
    }
  }, [confirmed, pendingLocation, history]);

  if (!pendingLocation) return null;

  if (variant === "cmp") {
    const saveAndUpdate = async () => {
      if (saving) return;
      setSaving(true);
      let saved = false;
      try { saved = onSaveAndUpdate ? await onSaveAndUpdate(pendingLocation.pathname + pendingLocation.search + pendingLocation.hash) : false; } finally { if (mounted.current) setSaving(false); }
      if (!mounted.current) return;
      if (saved) setConfirmed(true);
      else if (onSaveFailed) { setHidden(true); onSaveFailed(() => setConfirmed(true)); }
      else setPendingLocation(null);
    };
    if (hidden) return null;
    return (
      <CmpPopup label="Unsaved Changes" tone="warning" title="UNSAVED CHANGES" onClose={() => setPendingLocation(null)}>
        <div style={{ fontSize: 14, fontWeight: 600, marginTop: 8, lineHeight: 1.5 }}>Click Save &amp; Update before leaving or changes will be lost.</div>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 12, width: "100%", marginTop: 24 }}>
          <button type="button" onClick={saveAndUpdate} disabled={saving}
            style={{ ...cmpPopupButton(CMP_POPUP_COLORS.gold), cursor: saving ? "default" : "pointer", opacity: saving ? 0.7 : 1 }}>
            Save & Update
          </button>
          <button type="button" onClick={() => setConfirmed(true)} disabled={saving} style={cmpPopupButton(CMP_POPUP_COLORS.black)}>Leave Without Saving</button>
        </div>
      </CmpPopup>
    );
  }

  return (
    <ActionDialog
      open={Boolean(pendingLocation)}
      onClose={() => setPendingLocation(null)}
      icon={<img src={InformationIcon} alt="Information" style={{ width: 80, height: 80 }} />}
      iconBgColor="transparent"
      iconSize={80}
      title={title}
      description={description}
      buttonText={stayButtonText}
      onButtonClick={() => setPendingLocation(null)}
      secondaryButtonText={leaveButtonText}
      onSecondaryButtonClick={() => setConfirmed(true)}
      maxWidth="580px"
    />
  );
};

export default UnsavedChangesPrompt;
